import { Campaign, defaultImage, EdnaService, LogService, MediaPreApproval } from '@hub/shared'
import { SqButton, SqCallout, SqInput, SqInputMedia, SqNumbersHelper, SqOverlay, SqSelect, SqToastHelper } from '@squidit/react-css'
import heic2any from 'heic2any'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import './overlay-approve-content.component.scoped.scss'

export interface MapFiles {
  ext: string
  contentType: string
  fileName: string
}

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  campaign: Campaign
  mediaSelected?: MediaPreApproval
  newContentList?: File[]
  onCloseOverlay: VoidFunction
  mapFiles: (files: File[]) => MapFiles[]
  uploadFiles: (signedUrlArray: string[], files: File[]) => Promise<unknown>
  submitContentToApprove: (signedUrlArray: string[], content: MediaPreApproval, contentId: string) => Promise<unknown>
  getContents: () => Promise<void>
}

interface State {
  newContentList: File[]
  typeMedia: string
  description: string
  isEdit: boolean
  loading: boolean
}

interface StateValidation {
  typeMedia: boolean
  medias: boolean
  description: boolean
}

const OverlayApproveContent = ({
  campaign,
  open = true,
  setOpen,
  mediaSelected = null,
  newContentList = null,
  onCloseOverlay,
  mapFiles,
  uploadFiles,
  submitContentToApprove,
  getContents,
}: Props) => {
  const ednaService = useMemo(() => new EdnaService(), [])
  const numbersHelper = useMemo(() => new SqNumbersHelper(), [])
  const toast = useMemo(() => new SqToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('Overlay Approve Content Component')

  const initialState: State = useMemo(
    () => ({
      newContentList: newContentList || [],
      typeMedia: mediaSelected?.type || '',
      description: mediaSelected?.caption || '',
      isEdit: !!mediaSelected,
      loading: false,
    }),
    [mediaSelected, newContentList]
  )

  const [state, setState] = useState<State>(initialState)
  const [countContentLoaded, setCountContentLoaded] = useState(0)

  const initialValidation: StateValidation = useMemo(
    () => ({
      description: !!state?.description || state?.typeMedia === 'story',
      typeMedia: !!state?.typeMedia,
      medias: !!state?.newContentList?.length,
    }),
    [state]
  )

  const [validation, setValidation] = useState<StateValidation>(initialValidation)

  const { t } = useTranslation('overlayApproveContent')

  const isAllValid = useMemo(() => Object.values(validation).every(Boolean), [validation])
  const isRequired = useMemo(() => state?.typeMedia !== 'story' && state?.typeMedia !== '', [state])

  const closeOverlay = useCallback(() => {
    setState(initialState)
    setOpen(false)
    onCloseOverlay()
  }, [initialState, setOpen, onCloseOverlay])

  const handleSubmit = useCallback(
    async (medias, contentId = null) => {
      setState((prevState) => ({ ...prevState, loading: true }))
      setCountContentLoaded(0)

      try {
        await Promise.all(
          medias?.map(async (media) => {
            const response = await ednaService.generateSignedURL(campaign._id, mapFiles(media.files))
            await uploadFiles(response, media.files)
            await submitContentToApprove(response, media, contentId)
            setCountContentLoaded((prevState) => prevState + 1)
          })
        )

        await getContents()
        closeOverlay()
      } catch (error) {
        logService.error({ message: 'Failed to generate signed url', error })
        toast.error(t('errors.unableSubmitContent'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setState((prevState) => ({ ...prevState, loading: false }))
      }
    },
    [campaign._id, ednaService, submitContentToApprove, uploadFiles, closeOverlay, getContents, logService, t, toast, mapFiles]
  )

  const onSubmit = async () => {
    setState((prevState) => ({ ...prevState, loading: true }))
    const files = await Promise.all(
      state?.newContentList?.map(async (file) => {
        if (file?.type === 'image/heic' || file?.type === 'image/heif') {
          let newImage
          const m: any = await heic2any({ blob: file, toType: 'image/jpeg', quality: 1 })
          newImage = new File([m], file.name, { type: 'image/jpeg' })
          return newImage
        }
        return file
      })
    )

    const media = files?.map((file) => ({
      files: [file],
      legend: state?.description,
      type: state?.typeMedia,
    }))

    if (state?.isEdit) {
      await handleSubmit(media, mediaSelected?._id)
      return closeOverlay()
    }
    return handleSubmit(media)
  }

  const Header = () => (
    <div className="header-overlay-approve-page">
      <SqButton
        size="sm"
        inverted
        color="transparent"
        borderStyle="none"
        textColor="var(--text_color)"
        style={{ padding: 0 }}
        onClick={closeOverlay}
      >
        <i className="fa-regular fa-arrow-left lg" style={{ color: 'var(--white)' }}></i>
      </SqButton>
      <h5 className="m-0 white-html">{mediaSelected ? t('editContent') : t('newContent')}</h5>
    </div>
  )

  const Footer = () => (
    <div className="footer-overlay display-flex justify-content-space-between" style={{ width: '100%' }}>
      <SqButton
        size="sm"
        inverted
        color="transparent"
        borderColor="var(--text_color)"
        textColor="var(--text_color)"
        style={{ padding: '0.25rem 1rem' }}
        disabled={state?.loading}
        onClick={closeOverlay}
      >
        {t('cancel')}
      </SqButton>
      <SqButton disabled={state?.loading || !isAllValid} onClick={onSubmit} loading={state?.loading}>
        {t('save')}
      </SqButton>
    </div>
  )

  const optionsContent = [
    {
      type: 'post',
      label: t('typesContent.post'),
    },
    {
      type: 'story',
      label: t('typesContent.stories'),
    },
    {
      type: 'reels',
      label: t('typesContent.reels'),
    },
  ]

  const imagePreview = (file) => {
    if (file.type.includes('image')) {
      return URL.createObjectURL(file)
    }
    return defaultImage
  }

  const removeFile = useCallback(
    (file) => {
      const newList = state?.newContentList?.filter((f) => f.name !== file.name)
      setState((prevState) => ({ ...prevState, newContentList: newList }))
      let isMediaValid = false
      if (!!newList?.length) {
        isMediaValid = true
      }
      setValidation((prevValidation) => ({ ...prevValidation, medias: isMediaValid }))
    },
    [state?.newContentList]
  )

  const convertMedias = useCallback(async (medias) => {
    const modifiedMedias = await Promise.all(
      medias?.map(async (file) => {
        if (file?.type === 'image/heic' || file?.type === 'image/heif') {
          let newImage
          const m: any = await heic2any({ blob: file, toType: 'image/jpeg', quality: 1 })
          newImage = new File([m], file.name, { type: 'image/jpeg' })
          return newImage
        }
        return file
      })
    )

    return modifiedMedias
  }, [])

  const handleChangeMedia = useCallback(
    async (medias: FileList) => {
      if (medias?.length) {
        const mediasConverted = await convertMedias(Array.from(medias))
        setState((prevState) => ({ ...prevState, newContentList: [...prevState.newContentList, ...mediasConverted] }))
        let isMediaValid = false
        if (state?.newContentList?.length) {
          isMediaValid = true
        }
        setValidation((prevValidation) => ({ ...prevValidation, medias: isMediaValid }))
      }
    },
    [convertMedias, state?.newContentList]
  )

  useEffect(() => {
    setState(initialState)
  }, [initialState])

  useEffect(() => {
    setValidation(initialValidation)
  }, [initialValidation])

  return (
    <SqOverlay
      open={open}
      onCloseChange={closeOverlay}
      header={<Header />}
      footer={<Footer />}
      buttonClose={false}
      headerColor="var(--black)"
    >
      {open && (
        <div className="content-overlay">
          <SqSelect
            name="type-media-approve-select"
            id="type-media-approve-select"
            className="type-media-approve-select"
            placeholder=" "
            label={t('typeLabel')}
            value={Array.from(state?.newContentList)?.length > 1 ? 'story' : state?.typeMedia}
            onChange={(typeMedia: string) => {
              setState((prevState) => ({ ...prevState, typeMedia }))
              setValidation((prevValidation) => ({ ...prevValidation, description: typeMedia === 'story' || !!state?.description?.length }))
            }}
            onValidate={(typeMedia: boolean) => setValidation((prevValidation) => ({ ...prevValidation, typeMedia }))}
            disabled={state?.loading}
          >
            {optionsContent?.map((option, index) => (
              <option key={index} value={option?.type}>
                {option?.label}
              </option>
            ))}
          </SqSelect>

          {state.typeMedia ? (
            <SqCallout icon={'fa-regular fa-lightbulb-on'} colorIcon="var(--text-color)" iconFontSize="3rem" style={{ width: '100%' }}>
              <span dangerouslySetInnerHTML={{ __html: t(`warning.${state.typeMedia}`) }} />
            </SqCallout>
          ) : null}

          <div className="input-media">
            <label htmlFor="input-media-file">{t('media')}</label>
            <SqInputMedia
              label={
                <div className="input-media-labels">
                  <h5 className="m-0">{t('addMedia')}</h5>
                </div>
              }
              multiple
              resolution={{ width: 1080, height: 1980 }}
              name={'input-media-file'}
              id={'input-media-file'}
              onChange={async (medias: FileList) => handleChangeMedia(medias)}
              value={[]}
            />
            {!!Array.from(state?.newContentList)?.length && (
              <div className="images-preview">
                {Array.from(state?.newContentList)?.map((file, index) => (
                  <div className="card-media" key={index}>
                    <img src={imagePreview(file)} alt={file?.name} />
                    <div className="content-card-media">
                      <h6 className="m-0">{file?.name}</h6>
                      <span className="description-card-media">{numbersHelper.fileSize(file?.size)}</span>
                    </div>
                    <SqButton
                      size="sm"
                      inverted
                      color="transparent"
                      borderStyle="none"
                      textColor="var(--text_color)"
                      style={{ padding: '0.25rem 1rem' }}
                      onClick={() => removeFile(file)}
                      disabled={state?.loading}
                    >
                      <i className="fa-regular fa-trash lg" style={{ color: 'var(--primary_color)' }}></i>
                    </SqButton>
                  </div>
                ))}
              </div>
            )}
          </div>
          {state.loading && state?.newContentList?.length > 0 ? (
            <SqCallout>
              <div style={{ width: '100%' }}>
                <p>
                  {countContentLoaded} / {state?.newContentList?.length} {t('loadedContent', { count: state?.newContentList?.length })}
                </p>
                <div className="progress">
                  <div
                    className="progress-bar background-dashed"
                    role="progressbar"
                    style={{ width: `${(countContentLoaded / state?.newContentList?.length) * 100}%` }}
                    aria-valuenow={countContentLoaded}
                    aria-valuemin={0}
                    aria-valuemax={state?.newContentList?.length}
                  ></div>
                </div>
              </div>
            </SqCallout>
          ) : null}
          <SqInput
            label={t('caption')}
            value={state?.description || ''}
            readOnly={state.loading}
            type="textarea"
            className="description-media-approve-textarea"
            name="description-media-approve-textarea"
            id="description-media-approve-textarea"
            maxLength={2200}
            onChange={(description: string) => setState((prevState) => ({ ...prevState, description }))}
            onValidate={(description: boolean) => setValidation((prevValidation) => ({ ...prevValidation, description }))}
            required={isRequired}
          />
          <SqCallout icon={'fa-regular fa-lightbulb-on'} colorIcon="var(--text-color)" iconFontSize="3rem" style={{ width: '100%' }}>
            <span dangerouslySetInnerHTML={{ __html: t('warningHashtags') }} />
          </SqCallout>
        </div>
      )}
    </SqOverlay>
  )
}

export default OverlayApproveContent
